import { useMemo, useState } from 'react';
import { useGetScanHistory } from 'hooks/useGetScanHistory';
import { SortField, SortOrder } from '../types';

interface UseScansPageArgs {
	/** Poll interval (ms) threaded into the scan query, or `undefined` to disable — from `useAutoRefresh`. */
	refetchInterval: number | undefined;
}

/**
 * Drives the Scanned Tickets page: the per-ticket `/tickets/history` log with client-side search + sort.
 * `refetchInterval` (from the shared `useAutoRefresh`) threads the opt-in 60s poll into the query.
 * Full design: ./CLAUDE.md.
 */
export const useScansPage = ({ refetchInterval }: UseScansPageArgs) => {
	const history = useGetScanHistory(refetchInterval);

	const scansData = useMemo(() => history.data?.tickets ?? [], [history.data]);

	const [searchText, setSearchText] = useState('');
	const [sortBy, setSortBy] = useState<SortField>('scannedAt');
	const [order, setOrder] = useState<SortOrder>('desc');

	const handleSort = (field: SortField) => {
		if (sortBy === field) {
			setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
		} else {
			setSortBy(field);
			setOrder('desc');
		}
	};

	const filteredAndSorted = useMemo(() => {
		let result = scansData;

		if (searchText) {
			const lower = searchText.toLowerCase();
			result = result.filter((s) => s.fullName.toLowerCase().includes(lower) || s.email.toLowerCase().includes(lower));
		}

		return [...result].sort((a, b) => {
			let comparison = 0;
			if (sortBy === 'scannedAt') {
				comparison = (a.ticket.scannedAt ?? '').localeCompare(b.ticket.scannedAt ?? '');
			} else if (sortBy === 'fullName') {
				comparison = a.fullName.localeCompare(b.fullName);
			} else {
				comparison = a.ticketTypeName.localeCompare(b.ticketTypeName);
			}
			return order === 'asc' ? comparison : -comparison;
		});
	}, [scansData, searchText, sortBy, order]);

	// Seated events carry a seat per ticket; the Seat column is shown only then.
	const hasSeats = useMemo(() => scansData.some((s) => !!s.ticket.seatId), [scansData]);

	return {
		scans: filteredAndSorted,
		hasSeats,
		isSearching: searchText.trim().length > 0,
		isLoading: history.isLoading,
		isFetching: history.isFetching,
		isError: history.isError,
		// First-load failure vs background-refetch failure: the page shows a retryable error panel only
		// when the query has no data yet (a populated table stays put on a background error).
		hasData: history.data !== undefined,
		refetch: history.refetch,
		setSearchText,
		sortBy,
		order,
		handleSort,
	};
};
